import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * PART 22 — the PLATFORM OPERATOR CONSOLE.
 *
 * Every route here lives under `/ops`, and that prefix is not cosmetic: it is
 * the boundary. `/api/*` is the tenant product, authenticated by a tenant
 * session; `/ops/*` is the console, authenticated by an operator session from
 * a different table under a differently named cookie. A tenant session
 * presented here resolves to nothing, and an operator session presented at
 * `/api/*` resolves to nothing (BR-OPS-00-01).
 *
 * WHAT IS NOT IN THIS FILE IS THE POINT. There is no route that returns a
 * ledger, an invoice, a salary, an employee, a customer, an item, a stock
 * figure or a document, and there is no route that returns a tenant user's
 * name or email (§22.1, BR-OPS-00-05, BR-OPS-01-02). Seat usage is a COUNT.
 * Last activity is a TIMESTAMP. Neither names a person. There is also no
 * sign-in-as, no impersonation and no support-view: owner ruling R16 and
 * BR-OPS-00-10 forbid the console from ever holding a tenant session.
 *
 * There is likewise no route that CREATES an operator (BR-OPS-00-03) — the two
 * accounts arrive from the deployment bootstrap and from nowhere else.
 */
// ── Sign-in (OPS-00 — the console application itself) ───────────────────────
/**
 * BR-OPS-00-02 wants a registered HARDWARE key on every sign-in, with no path
 * around it. A hardware key cannot be exercised in local development or CI, so
 * `totp` stands in as the second factor — and it is genuinely required: there
 * is no password-only branch below, and no recovery code that works without
 * it. TODO(webauthn): swap this field for the assertion at deploy, keeping the
 * "second factor or no session" shape exactly as it is.
 */
export const OperatorSignInSchema = z
    .object({
    email: z.string().min(3).max(320),
    password: z.string().min(1).max(200),
    totp: z.string().regex(/^\d{6}$/),
})
    .strict();
export const OperatorSchema = z
    .object({ id: z.uuid(), name: z.string(), email: z.string() })
    .strict();
// ── OPS-01 — tenant list ────────────────────────────────────────────────────
export const TENANT_STATUSES = ['active', 'suspended', 'pending'];
export const PAID_MODULES = ['manufacturing', 'projects', 'multi_branch'];
export const PaidModuleSchema = z.enum(PAID_MODULES);
// ── R21 — subscriptions ─────────────────────────────────────────────────────
/**
 * HOW a company got its period. R21 names two routes and this build adds a
 * third that is not a route at all — the migration that introduced
 * subscriptions had to give every existing tenant one, and labelling those as
 * operator grants would have put words in an operator's mouth.
 */
export const GRANT_METHODS = ['online_payment', 'operator_grant', 'backfill'];
export const GrantMethodSchema = z.enum(GRANT_METHODS);
/**
 * WHERE A COMPANY STANDS, in three words and no more.
 *
 *   current — a period covers right now; everybody can sign in.
 *   expired — there was a period and it has ended, or an operator revoked it.
 *   none    — there has never been one. A self-signup that could not pay
 *             lands here, and so does a company an operator created but has
 *             not yet granted.
 *
 * `expired` and `none` are the same refusal to the customer and different
 * facts to the operator, which is why they are two words rather than one.
 */
export const SUBSCRIPTION_STATES = ['current', 'expired', 'none'];
export const SubscriptionStateSchema = z.enum(SUBSCRIPTION_STATES);
/** One period, as the console shows it. Names operators — never tenant users. */
export const SubscriptionSchema = z
    .object({
    id: z.uuid(),
    plan: z.string(),
    modules: z.array(PaidModuleSchema),
    periodStart: z.string(),
    /** Exclusive: access runs up to, and not including, this instant. */
    periodEnd: z.string(),
    grantedVia: GrantMethodSchema,
    /** The OPERATOR who granted it. Null on an online payment and a backfill. */
    grantedByName: z.string().nullable(),
    paymentReference: z.string().nullable(),
    note: z.string().nullable(),
    revokedAt: z.string().nullable(),
    revokedByName: z.string().nullable(),
    revokedReason: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
/**
 * §22.5 — grant a period. THIS IS THE OWNER'S DAY-TO-DAY TOOL: the route by
 * which a customer who paid cash at a local office, or bought over the phone,
 * or is simply being let in to try the system, gets access. R21 puts all three
 * on the same path on purpose — there is no separate "trial" anything.
 */
export const GrantSubscriptionSchema = z
    .object({
    plan: z.string().min(1).max(60),
    /**
     * Days rather than months, because a month is not a fixed length and the
     * end of a period is a moment somebody gets locked out at. The console
     * offers the ordinary lengths as buttons; the field takes any of them.
     */
    durationDays: z.number().int().min(1).max(3650),
    /** Which paid modules this period buys. Empty is legitimate — the core product. */
    modules: z.array(PaidModuleSchema).max(3),
    /** "Paid cash at the Ramallah office." The answer to "why does this company have access". */
    note: z.string().max(500).optional(),
})
    .strict();
export const ExtendSubscriptionSchema = z
    .object({
    durationDays: z.number().int().min(1).max(3650),
    note: z.string().max(500).optional(),
})
    .strict();
/**
 * Revoking cuts a working company off mid-period, so it asks for the same
 * typed name BR-OPS-01-07 asks for before a suspension, and for a reason —
 * "revoked, no idea why" is not a state the audit log should be able to hold.
 */
export const RevokeSubscriptionSchema = z
    .object({
    confirmName: z.string().min(1).max(200),
    reason: z.string().min(1).max(500),
})
    .strict();
/** One row of OPS-01. Read the column list against §22.1: every field is a
 *  count, a size, a date, a status or an entitlement. */
export const TenantRowSchema = z
    .object({
    id: z.uuid(),
    companyName: z.string(),
    createdAt: z.string(),
    plan: z.string(),
    /** The three marks of the Paid modules column. */
    modules: z.object({
        manufacturing: z.boolean(),
        projects: z.boolean(),
        multi_branch: z.boolean(),
    }),
    /** `7 / 10` — a COUNT against the limit, never a list of names. */
    seatsUsed: z.number().int(),
    seatLimit: z.number().int(),
    storageBytes: z.number().int(),
    status: z.enum(TENANT_STATUSES),
    /**
     * R21, on the list rather than only on the detail page: the operator's
     * first question about any company is whether it can still sign in, and
     * making them open twenty rows to find out is how renewals get missed.
     */
    subscriptionState: SubscriptionStateSchema,
    /** When access ends (or ended). Null when there has never been a period. */
    subscriptionEndsAt: z.string().nullable(),
    /** A TIMESTAMP, never a person (BR-OPS-01-02). Null until somebody signs in. */
    lastActivityAt: z.string().nullable(),
    /** Set while a deletion's grace period runs; nothing is destroyed yet. */
    deletionScheduledFor: z.string().nullable(),
})
    .strict();
export const CreateTenantSchema = z
    .object({
    companyName: z.string().min(1).max(200),
    country: z.string().max(60).optional(),
    residencyRegion: z.string().min(1).max(60),
    plan: z.string().min(1).max(60),
    /** ERR-OPS-01-05: "A company needs at least one seat." */
    seatLimit: z.number().int().min(1).max(10_000),
    ownerName: z.string().min(1).max(120),
    /** BR-OPS-01-03: exactly one invitation goes here, and no password is created. */
    ownerEmail: z.string().min(3).max(320),
})
    .strict();
/** BR-OPS-01-07: suspend and reactivate each require the company name TYPED. */
export const ConfirmNameSchema = z.object({ confirmName: z.string().min(1).max(200) }).strict();
// ── OPS-02 — tenant detail ──────────────────────────────────────────────────
export const TenantDetailSchema = TenantRowSchema.extend({
    country: z.string().nullable(),
    residencyRegion: z.string(),
    suspendedAt: z.string().nullable(),
    lastExportAt: z.string().nullable(),
    /** Whether the owner flag has a holder yet — a BOOLEAN, never the address. */
    hasOwner: z.boolean(),
    /** R21 — the period covering right now, or null if none does. */
    subscription: SubscriptionSchema.nullable(),
    /**
     * Every period this company has ever held, newest first. Extending appends
     * rather than editing, so this is a readable history: who granted what, for
     * how long, and why — which is exactly the question an operator asks when a
     * customer phones to argue about a renewal.
     */
    subscriptionHistory: z.array(SubscriptionSchema),
    /** Per-toggle provenance: §22.5 B wants "who last changed it, and when". */
    moduleHistory: z.array(z
        .object({
        module: PaidModuleSchema,
        enabled: z.boolean(),
        changedByName: z.string().nullable(),
        changedAt: z.string().nullable(),
    })
        .strict()),
    /**
     * Size and time and stop (BR-OPS-02-09) — never what is inside.
     *
     * `pending` is what an export is BORN as: §22.5 E says producing it "runs as
     * a background job with a visible state", and this is the state the screen
     * shows while the worker is getting to it.
     */
    exports: z.array(z
        .object({
        id: z.uuid(),
        state: z.enum(['pending', 'running', 'complete', 'failed']),
        startedAt: z.string(),
        completedAt: z.string().nullable(),
        sizeBytes: z.number().int().nullable(),
    })
        .strict()),
}).strict();
export const UpdateTenantSchema = z
    .object({
    companyName: z.string().min(1).max(200).optional(),
    country: z.string().max(60).optional(),
    plan: z.string().min(1).max(60).optional(),
    seatLimit: z.number().int().min(1).max(10_000).optional(),
})
    .strict();
/**
 * BR-OPS-02-06. The receiving account is named by the ADDRESS the tenant put
 * in writing — the console cannot browse for it, and `requestReference` is
 * required because §22.5 D makes recording what the tenant sent the first step
 * of the gate rather than a note afterwards.
 */
export const OwnerTransferSchema = z
    .object({
    requestReference: z.string().min(1).max(500),
    toEmail: z.string().min(3).max(320),
})
    .strict();
export const ExportRequestSchema = z
    .object({
    /** BR-OPS-02-08 / OQ-22-06: which channel carried the passphrase. Recorded, never the passphrase. */
    passphraseChannel: z.string().min(1).max(120),
})
    .strict();
/**
 * BR-OPS-00-08's gate, as a body: an export must already exist and post-date
 * the last activity, the company name must be typed, and the second
 * confirmation must acknowledge the grace period and its date.
 */
export const ScheduleDeletionSchema = z
    .object({
    confirmName: z.string().min(1).max(200),
    acknowledgeGracePeriod: z.literal(true),
})
    .strict();
export const DeletionScheduleSchema = z
    .object({ tenantId: z.uuid(), deletionScheduledFor: z.string(), graceDays: z.number().int() })
    .strict();
// ── OPS-03 — platform health ────────────────────────────────────────────────
/**
 * BR-OPS-03-05: "Every figure on this screen must carry the moment it was
 * measured. A stale number that looks live is worse than an empty card." So
 * every group carries `measuredAt`, and `available` distinguishes "zero" from
 * "no reading" — which the edge case in §22.6 insists are different facts.
 */
const HealthGroup = (rows) => z.object({ available: z.boolean(), measuredAt: z.string().nullable(), rows }).strict();
export const PlatformHealthSchema = z
    .object({
    tenantSizes: HealthGroup(z.array(z.object({ tenantId: z.uuid(), companyName: z.string(), storageBytes: z.number().int() }).strict())),
    totalStorageBytes: z.number().int(),
    /** BR-OPS-03-02: route, tenant, status and time — never a body or a field value. */
    errorRates: HealthGroup(z.array(z.object({ routeGroup: z.string(), statusCode: z.number().int(), perMinute: z.string(), tenantId: z.uuid().nullable() }).strict())),
    /** BR-OPS-03-03: type, tenant, age and failure count — never the payload. */
    jobQueues: HealthGroup(z.array(z.object({ queue: z.string(), depth: z.number().int(), oldestWaitingSeconds: z.number().int(), deadLettered: z.number().int() }).strict())),
    /** BR-OPS-03-04: the restore test is its OWN figure, and can be overdue. */
    backups: z
        .object({
        available: z.boolean(),
        measuredAt: z.string().nullable(),
        lastBackupAt: z.string().nullable(),
        lastBackupBytes: z.number().int().nullable(),
        lastRestoreTestAt: z.string().nullable(),
        restoreTestOverdue: z.boolean(),
    })
        .strict(),
})
    .strict();
// ── OPS-04 — operator audit log ─────────────────────────────────────────────
export const OperatorAuditRowSchema = z
    .object({
    id: z.uuid(),
    tsUtc: z.string(),
    operatorName: z.string(),
    action: z.string(),
    /** Kept as plain text so the row still reads after the tenant is gone. */
    tenantId: z.uuid().nullable(),
    tenantNameAtTime: z.string().nullable(),
    changes: z.record(z.string(), z.unknown()),
    source: z.enum(['console', 'script']),
    outcome: z.enum(['accepted', 'refused']),
    requestId: z.string().nullable(),
})
    .strict();
/**
 * The export ceiling — ERR-OPS-04-01's "export limit". The same figure as the
 * tenant log's `AUDIT_EXPORT_MAX`, for the same reason: not a page size but a
 * refusal to stream an unbounded result set into one response. A range that
 * still holds more than this is one the operator needs to narrow, and the
 * refusal names the count so the advice is actionable.
 */
export const OPS_AUDIT_EXPORT_MAX = 5000;
/**
 * §22.7's filter set — by operator, by tenant, by action, and by date range —
 * shared between the list and the export so "the currently applied filters"
 * cannot drift from what the export honours (and logs, BR-OPS-04-05).
 */
export const OperatorAuditFilterSchema = z
    .object({
    operatorId: z.uuid().optional(),
    tenantId: z.uuid().optional(),
    action: z.string().max(80).optional(),
    from: IsoDateSchema.optional(),
    to: IsoDateSchema.optional(),
})
    .strict();
/**
 * ── THE FOUR ERRORS THAT MUST NEVER BE CITED IN THE CUSTOMER APPLICATION ───
 *
 * PART 15.3's screen carries four error identifiers, and a coverage sweep will
 * keep finding them in the "named nowhere" queue and keep reading them as work
 * to do. They are not. That section's own opening says so:
 *
 *   "Read this section as the prototype's record, not as a screen to build
 *    here … Nothing in this section may be built inside the customer
 *    application."
 *
 * Owner ruling R19 (2 Aug 2026) moved the platform operator panel out of the
 * customer product entirely and made it PART 22 — a separate application, a
 * separate login with hardware-key MFA, no access to tenant business data.
 * The BEHAVIOUR those four rows describe is built, right here, under the
 * identifiers of the console that actually has the screen: the seat-cap
 * refusal is `seatBelowUsage` / ERR-OPS-02-04 below, worded for an operator
 * rather than for the customer who could never reach it.
 *
 * So they are not a gap in coverage. They are a section the specification
 * PROHIBITS building, and a sweep optimising for the counter takes them and
 * thereby records the customer application doing the one thing that section
 * forbids. The `rule-coverage:ignore` marks below are what stop the next
 * sweep rediscovering them as opportunities — each mark names one id and says
 * why it is not cited, which is exactly what the escape hatch is for.
 *
 *   rule-coverage:ignore ERR-ADM-09-01 — a customer reaching the console's
 *     address. PART 22's OperatorGuard answers this; the customer app has no
 *     such route to refuse.
 *   rule-coverage:ignore ERR-ADM-09-02 — a duplicate company name. An
 *     operator act, in the console, on a table the customer app cannot write.
 *   rule-coverage:ignore ERR-ADM-09-03 — a seat cap below the seats in use.
 *     Built as ERR-OPS-02-04 (`seatBelowUsage`), a few lines below.
 *   rule-coverage:ignore ERR-ADM-09-04 — the billing system not answering
 *     while creating a company. Again an operator act, not a tenant one.
 */
export const OPS_MESSAGES = {
    signedOut: 'Sign in to the operator console.',
    badCredentials: 'That email, password and code do not match.',
    disabled: 'This operator account is switched off.',
    /** ERR-OPS-01-01 — the name is echoed so the operator can see what to type. */
    nameMismatch: (name) => `That name does not match. Type ${name} to confirm.`,
    /** ERR-OPS-01-02 */
    emailOwnsTenant: 'This address already owns a company on the platform. Use a different address, or transfer that company instead.',
    /** ERR-OPS-01-03 */
    emailIsOperator: 'This address belongs to a platform operator account. Operator accounts cannot hold tenant accounts (BR-OPS-00-04).',
    /** ERR-OPS-01-04 */
    alreadySuspended: (on, by) => `This company is already suspended. It was suspended on ${on} by ${by}.`,
    notSuspended: 'This company is not suspended.',
    /** ERR-OPS-01-05 */
    seatFloor: 'A company needs at least one seat.',
    /** ERR-OPS-02-01 */
    exportFirst: "Export this company's data before deleting it. The last export was taken before their most recent activity.",
    /** ERR-OPS-02-02 */
    accountNotInTenant: 'That account does not belong to this company. The owner flag can only move to an account inside it.',
    /** ERR-OPS-02-03 */
    transferNeedsReference: 'Record what the company sent, and who sent it, before starting a transfer.',
    /** ERR-OPS-02-04 */
    seatBelowUsage: (used) => `This company has ${used} active accounts. The limit cannot go below ${used} until they remove accounts themselves.`,
    /** ERR-OPS-02-05 */
    noOwnerYet: 'This company has no Owner yet. Resend the invitation instead.',
    /** ERR-OPS-02-06 */
    exportRunning: 'An export of this company is already running.',
    /** ERR-OPS-02-07 */
    midDeletion: (on) => `This company is scheduled for deletion on ${on}. Cancel the deletion before changing anything.`,
    notScheduled: 'This company is not scheduled for deletion.',
    tenantNotFound: 'No such company.',
    /** R21 — extending is defined against an existing period; there is none to extend. */
    noSubscriptionToExtend: 'This company has no subscription to extend. Grant one instead — it is the same act, and it says who granted it and why.',
    /** R21 — revoking a period that is not running would record an event that did not happen. */
    noSubscriptionToRevoke: 'This company has no current subscription to revoke.',
    /** The one sentence the console says when the operator tries to sell a module that does not exist. */
    unknownModule: 'That is not one of the paid modules.',
    /** ERR-OPS-04-02 — the sentence that makes the log trustworthy. */
    auditWriteFailed: 'This action was stopped because it could not be recorded. Nothing was changed.',
    /**
     * ERR-OPS-04-01 — "An export is requested over a range with more entries
     * than the export limit." The spec's row names the COUNT the range holds
     * ("That range holds 240,000 entries."), not the limit — the number that
     * tells the operator how far they are from fitting, which is what makes
     * "narrow the dates or the filters" actionable. Parameterised, and formatted
     * with thousands separators exactly as the row writes 240,000: the console
     * is English-only by design, so `en-US` is the locale rather than a guess.
     */
    auditExportTooLarge: (entries) => `That range holds ${entries.toLocaleString('en-US')} entries. Narrow the dates or the filters.`,
};
// ── The contract ────────────────────────────────────────────────────────────
export const opsContract = c.router({
    signIn: {
        method: 'POST',
        path: '/ops/auth/sign-in',
        body: OperatorSignInSchema,
        responses: { 200: z.object({ operator: OperatorSchema }).strict(), 401: MessageSchema },
        summary: 'OPS-00 — password AND second factor. No password-only branch exists (BR-OPS-00-02)',
    },
    signOut: {
        method: 'POST',
        path: '/ops/auth/sign-out',
        body: c.noBody(),
        responses: { 204: c.noBody() },
        summary: 'OPS-00 — ends the operator session',
    },
    me: {
        method: 'GET',
        path: '/ops/me',
        responses: { 200: z.object({ operator: OperatorSchema }).strict(), 401: MessageSchema },
        summary: 'OPS-00 — who is signed in to the console',
    },
    listTenants: {
        method: 'GET',
        path: '/ops/tenants',
        query: z.object({
            search: z.string().max(200).optional(),
            status: z.enum(TENANT_STATUSES).optional(),
            plan: z.string().max(60).optional(),
            module: PaidModuleSchema.optional(),
            createdFrom: IsoDateSchema.optional(),
            createdTo: IsoDateSchema.optional(),
        }),
        responses: { 200: z.object({ tenants: z.array(TenantRowSchema), total: z.number().int() }).strict() },
        summary: 'OPS-01 — every tenant including suspended ones (BR-OPS-01-01)',
    },
    createTenant: {
        method: 'POST',
        path: '/ops/tenants',
        body: CreateTenantSchema,
        responses: { 201: TenantRowSchema, 400: MessageSchema, 409: MessageSchema },
        summary: 'OPS-01 — creates the tenant, seeds its roles, sends ONE invitation, creates no password',
    },
    getTenant: {
        method: 'GET',
        path: '/ops/tenants/:tenantId',
        pathParams: z.object({ tenantId: z.uuid() }),
        responses: { 200: TenantDetailSchema, 404: MessageSchema },
        summary: 'OPS-02 — one tenant, platform metadata only',
    },
    updateTenant: {
        method: 'PATCH',
        path: '/ops/tenants/:tenantId',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: UpdateTenantSchema,
        responses: { 200: TenantDetailSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §A/C — metadata and seats; a seat limit never goes below the seats in use (BR-OPS-02-13)',
    },
    suspendTenant: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/suspend',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: ConfirmNameSchema,
        responses: { 200: TenantDetailSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-01 — blocks sign-in, ends live sessions, DELETES NOTHING (BR-OPS-01-05/-06)',
    },
    reactivateTenant: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/reactivate',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: ConfirmNameSchema,
        responses: { 200: TenantDetailSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-01 — lifts a suspension; access returns on the next request',
    },
    setModule: {
        method: 'PUT',
        path: '/ops/tenants/:tenantId/modules/:module',
        pathParams: z.object({ tenantId: z.uuid(), module: PaidModuleSchema }),
        body: z.object({ enabled: z.boolean() }).strict(),
        responses: { 200: TenantDetailSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §B — Phase-1 billing. Takes effect on the tenant’s NEXT request (BR-OPS-02-02)',
    },
    // ── R21 — subscriptions. The route by which cash, phone and try-it
    //    customers get access, and the only route besides an online payment. ──
    grantSubscription: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/subscription',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: GrantSubscriptionSchema,
        responses: { 201: TenantDetailSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'R21 — grants a period starting now. Audited; takes effect on the tenant’s next request',
    },
    extendSubscription: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/subscription/extend',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: ExtendSubscriptionSchema,
        responses: { 200: TenantDetailSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'R21 — appends a period. Restores access IMMEDIATELY when the last one had lapsed',
    },
    revokeSubscription: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/subscription/revoke',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: RevokeSubscriptionSchema,
        responses: { 200: TenantDetailSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'R21 — ends the current period now. Blocks sign-in, deletes nothing (BR-OPS-01-06’s promise)',
    },
    resendInvitation: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/support/resend-invitation',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: c.noBody(),
        responses: { 202: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §D — resends the pending invitation to the SAME address; creates no account',
    },
    sendPasswordReset: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/support/password-reset',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: c.noBody(),
        responses: { 202: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §D — triggers the product’s OWN reset email. The operator never sees or sets a password (BR-OPS-02-05)',
    },
    startOwnerTransfer: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/support/owner-transfer',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: OwnerTransferSchema,
        responses: { 202: MessageSchema, 400: MessageSchema, 404: MessageSchema },
        summary: 'OPS-02 §D — starts a transfer; it never completes on the operator’s action alone (BR-OPS-02-06)',
    },
    createExport: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/exports',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: ExportRequestSchema,
        responses: { 202: TenantDetailSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §E — an encrypted archive. The console never previews it (BR-OPS-02-09)',
    },
    /**
     * §22.5 E: "The console offers download and delivery, never a preview,
     * never a file browser, never a row count by table."
     *
     * This is the download. What comes back is CIPHERTEXT — the operator cannot
     * open it, because the passphrase went to the tenant's Owner by another
     * channel (BR-OPS-02-08). There is deliberately no route that decrypts,
     * lists, or samples it: "Reading it would walk straight through §22.1."
     */
    downloadExport: {
        method: 'GET',
        path: '/ops/tenants/:tenantId/exports/:exportId/content',
        pathParams: z.object({ tenantId: z.uuid(), exportId: z.uuid() }),
        responses: {
            200: c.otherResponse({ contentType: 'application/octet-stream', body: z.unknown() }),
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'OPS-02 §E — download the ENCRYPTED archive. Never a preview (BR-OPS-02-09)',
    },
    scheduleDeletion: {
        method: 'POST',
        path: '/ops/tenants/:tenantId/deletion',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: ScheduleDeletionSchema,
        responses: { 200: DeletionScheduleSchema, 400: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §E — export-first, name typed, second confirmation, grace period (BR-OPS-00-08)',
    },
    cancelDeletion: {
        method: 'DELETE',
        path: '/ops/tenants/:tenantId/deletion',
        pathParams: z.object({ tenantId: z.uuid() }),
        body: c.noBody(),
        responses: { 200: TenantDetailSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'OPS-02 §E — cancellable by EITHER operator at any point (BR-OPS-02-11)',
    },
    health: {
        method: 'GET',
        path: '/ops/health',
        responses: { 200: PlatformHealthSchema },
        summary: 'OPS-03 — read-only. No action on this screen changes anything (BR-OPS-03-01)',
    },
    auditLog: {
        method: 'GET',
        path: '/ops/audit',
        query: OperatorAuditFilterSchema.extend({
            limit: z.coerce.number().int().min(1).max(500).optional(),
        }),
        responses: { 200: z.object({ entries: z.array(OperatorAuditRowSchema), total: z.number().int() }).strict() },
        summary: 'OPS-04 — append-only, newest first. Readable by the two operators and by nobody else',
    },
    /**
     * §22.7: "Export to CSV, and THE EXPORT IS ITSELF AN ENTRY (this is
     * BR-PERM-53 applied to the operator's log)." BR-OPS-04-05 fixes what that
     * entry carries — the operator, the filters applied, and the number of
     * entries taken. A POST rather than a GET because running it writes that
     * entry: an export of the log is an operator action, not a safe read.
     */
    exportAuditLog: {
        method: 'POST',
        path: '/ops/audit/export',
        body: OperatorAuditFilterSchema,
        responses: {
            200: z.object({ csv: z.string(), exported: z.number().int() }).strict(),
            /** ERR-OPS-04-01 — the range holds more entries than OPS_AUDIT_EXPORT_MAX. */
            413: MessageSchema,
        },
        summary: 'OPS-04 — CSV of the filtered log. The export is itself an entry (BR-OPS-04-05)',
    },
});
//# sourceMappingURL=ops.js.map